import React from 'react';
import { Popover, Card, Button, Form } from 'antd';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import * as Yup from 'yup';
import FormSelect from '../../../global/Forms/FormSelect';
import FormInput from '../../../global/Forms/FormInput';

type AddDynamicFieldListPopoverProps = {
  createList: (name: string, valueType: 'string' | 'numeric') => void;
  onOpen?: () => void;
  onClose?: () => void;
  children: React.ReactNode;
};

const AddDynamicFieldListPopover: React.FC<AddDynamicFieldListPopoverProps> = props => {
  const { t } = useTranslation();

  const [popoverVisible, setPopoverVisible] = React.useState(false);

  React.useEffect(() => {
    setTimeout(() => {
      const input = document.getElementById('listNameInput');
      if (popoverVisible && input) input.focus();
    }, 100);
  }, [popoverVisible]);

  const handleVisibleChange = (visible: boolean) => {
    setPopoverVisible(visible);
    if (visible && props.onOpen) props.onOpen();
    else if (props.onClose) props.onClose();
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('validation:required')),
  });

  return (
    <Popover
      open={popoverVisible}
      onOpenChange={visible => handleVisibleChange(visible)}
      placement="bottomLeft"
      destroyOnHidden
      content={
        <Card className="dynamic-field-list__add-popover" variant="borderless">
          <Formik
            initialValues={{
              name: '',
              valueType: 'string',
            }}
            validationSchema={validationSchema}
            onSubmit={(values, formikActions) => {
              const { setSubmitting } = formikActions;
              setSubmitting(true);

              props.createList(values.name, values.valueType as 'string' | 'numeric');

              setSubmitting(false);
              handleVisibleChange(false);
            }}
          >
            {({ handleSubmit, dirty, isSubmitting }) => {
              if (!popoverVisible) return null;

              return (
                <div>
                  <Form layout="vertical">
                    <FormInput
                      id="listNameInput"
                      name="name"
                      label={t('dynamicFieldList:listName')}
                      required
                    />
                    <FormSelect
                      name="valueType"
                      values={[
                        { id: 'string', name: 'string' },
                        { id: 'numeric', name: 'numeric' },
                      ]}
                      label={t('dynamicFieldList:valueType')}
                      required
                    />
                  </Form>

                  <div className="flex-col items-end">
                    <div>
                      <Button
                        onClick={() => handleVisibleChange(false)}
                        className="drawer-submit__bottom-cancel"
                      >
                        {t('common:cancel')}
                      </Button>
                      <Button
                        type="primary"
                        onClick={() => handleSubmit()}
                        disabled={!dirty || isSubmitting}
                        data-testid="ok"
                      >
                        {t('common:ok')}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            }}
          </Formik>
        </Card>
      }
      trigger="click"
    >
      {props.children}
    </Popover>
  );
};

export default AddDynamicFieldListPopover;
